import React from "react";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem, treeItemClasses } from "@mui/x-tree-view/TreeItem";
import { Typography, Box, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import List from "@mui/material/List";
import ListItem, { listItemClasses } from "@mui/material/ListItem";
import MzTooltip from "@markaaz/ui/MzTooltip";
import MzIcon from "mz/MzIcon";
import MzInput from "@markaaz/ui/MzInputControls";
import { Formik, Form, Field } from "formik";


const sx = {
  treeItem: {
    svg: {
      color: "blue.c100",
      width: "24px",
      height: "24px"
    },
    [`& .${treeItemClasses.content}`]: {
      paddingLeft: "40px"
    },
    [`.${treeItemClasses.content}.Mui-selected`]: {
      backgroundColor: "blue.c5"
    },
    [`.${treeItemClasses.content}.Mui-focused`]: {
      backgroundColor: "blue.c5"
    },
    [`.${treeItemClasses.content}.Mui-selected.Mui-focused`]: {
      backgroundColor: "blue.c5"
    },
    [`.${treeItemClasses.content}.Mui-selected.Mui-focused:hover`]: {
      backgroundColor: "blue.c5"
    },
    [`.${treeItemClasses.content}`]: {
      minHeight: "30px",
      maxHeight: "100px",
      "&:hover": {
        backgroundColor: "blue.c5"
      }
    },
    ".MuiSvgIcon-root": {
      boxSizing: "content-box",
      paddingLeft: "8px"
    },
    ".MuiIconButton-root .MuiSvgIcon-root" : {
      paddingLeft: "0px"
    }
  },
  label: {
    cursor: "text"
  },
  labelEdit: {
    display: "flex",
    cursor: "text"
  },
  tooltip: {
    "& .MuiTooltip-tooltip": {
      maxWidth: "471px"
    }
  },
  dataChangeIcon: {
    fill: (theme) => theme.palette.common.pending,
  },
  dataChangeTooltip: {
    "& .MuiTooltip-tooltip": {
      color: (theme) => theme.palette.darkBlue.c100,
      backgroundColor: (theme) => theme.palette.common.pending,
      maxWidth: "471px"
    },
    "& .MuiTooltip-arrow": {
      color: (theme) => theme.palette.common.pending,
    }
  },
  labelWrap: {
    display: "flex",
    alignItems: "center"
  },
  list: {
    listStyleType: "disc",
    margin: "16px 20px",
    padding: 0,
    [`.${listItemClasses.root}`]: {
      display: "list-item",
      padding: 0
    }
  },
  iconButton: {
    padding: "0px 0px 0px 8px",
    "&:hover": {
      backgroundColor: "blue.c5"
    }
  },
  input: {
    height: "26px", 
    "& .MuiInputBase-root": {
      height: "26px",
      minHeight: "26px",
      margin: "0px 0px 0px 8px",
    },
    "& .MuiInputBase-input": {
      height: "26px",
      padding: '0px 0px 0px 8px',
    },
    "& .MuiOutlinedInput-root .MuiInputBase-input": {
      height: "26px",
      padding: '0px 0px 0px 8px',
    },
    "& .MuiOutlinedInput-input": {
      height: "26px",
      padding: '0px 0px 0px 8px',
    }
  },
  spinner: { 
    minWidth: '24px', 
    minHeight: '24px' 
  }
};

export default function MarkaazMatchFoundDetails({ markaazId, data, matchFoundTooltipData, editingKey, isSaving, onEditClick, onSaveClick, onCancelClick, goldenRecordKeys, goldenRecordChanges }) {
  if (data == null) return null;

  function renderTree(data, parentIndex = "") {
    return Object.entries(data).map(([key, value], outerIndex) => {
      if (key.startsWith("_") || value === undefined || key === "Address Type:") return null;

      let labelValue = "";

      if (Array.isArray(value)) {
        labelValue = `${key} (${value?.length})`;
      } else if (typeof value === "object" && value !== null) {
        labelValue = value._label || key;
      } else {
        labelValue = `${key} ${value}`;
      }
      
      const itemKey = `${markaazId}-${key}`
      const cleanedKey = key.slice(0,-1)

      return (
        <TreeItem
          sx={sx.treeItem}
          itemId={`${parentIndex}${outerIndex}`}
          label={
            <Box sx={sx.labelWrap}>
              {editingKey && editingKey === itemKey && (
                <>
                  <Typography sx={sx.labelEdit} variant="body2">
                    {key}
                    <Formik
                      initialValues={{ [itemKey]: value || '' }}
                      enableReinitialize
                      onSubmit={values => {
                        onSaveClick(values[itemKey])
                      }}
                    >
                      {({ handleSubmit }) => (
                        <Form onSubmit={handleSubmit}>
                          <Field
                            sx={sx.input}
                            name={itemKey}
                            as={MzInput}
                            onMouseDown={e => e.stopPropagation()}
                            onClick={e => e.stopPropagation()}
                            onKeyDown={e => e.stopPropagation()}
                            disabled={isSaving}
                          />
                          <IconButton sx={sx.iconButton} type="submit" disabled={isSaving}>
                            { isSaving ? <Box className="k-i-loading" sx={sx.spinner}></Box> : <SaveIcon />}
                          </IconButton>
                          <IconButton
                            sx={sx.iconButton}
                            onClick={() => {
                              onCancelClick()
                            }}
                            disabled={isSaving}
                          >
                            <ClearIcon />
                          </IconButton>
                        </Form>
                      )}
                    </Formik>
                  </Typography>
                </>
              )}
              {!(editingKey && editingKey === itemKey) && (
                <>
                  <Typography sx={sx.label} variant="body2">
                    {labelValue}
                  </Typography>
                  {goldenRecordKeys && goldenRecordKeys.includes(key) && (
                    <MzTooltip
                      sx={sx.tooltip}
                      title={`Edit ${cleanedKey}`}
                      arrow
                      placement="top"
                      PopperProps={{
                        modifiers: [{ name: 'preventOverflow', options: { padding: 20 } }],
                      }}
                    >
                      <IconButton
                        sx={sx.iconButton}
                        onClick={() => {
                          onEditClick(markaazId, key, itemKey)
                        }}
                        disabled={isSaving}
                      >
                        <MzIcon name="EditIcon" />
                      </IconButton>
                    </MzTooltip>
                  )}
                  {goldenRecordChanges && key in goldenRecordChanges && (
                    <MzTooltip
                      sx={sx.dataChangeTooltip}
                      title={
                        goldenRecordChanges[key].updatedValue !== null
                          ? `"${goldenRecordChanges[key].actualValue}" → "${goldenRecordChanges[key].updatedValue}" (by ${goldenRecordChanges[key].updatedBy})`
                          : `No changes`
                      }
                      arrow
                      placement="top"
                      PopperProps={{
                        modifiers: [{ name: 'preventOverflow', options: { padding: 20 } }],
                      }}
                    >
                      <InfoOutlinedIcon sx={sx.dataChangeIcon}></InfoOutlinedIcon>
                    </MzTooltip>
                  )}
                  {matchFoundTooltipData != null && key in matchFoundTooltipData && (
                    <MzTooltip
                      sx={sx.tooltip}
                      title={
                        Array.isArray(matchFoundTooltipData[key]) ? (
                          <List sx={sx.list}>
                            {matchFoundTooltipData[key].map((value, index) => (
                              <ListItem key={index}>{value}</ListItem>
                            ))}
                          </List>
                        ) : (
                          matchFoundTooltipData[key]
                        )
                      }
                      arrow
                      placement="top"
                      PopperProps={{
                        modifiers: [{ name: 'preventOverflow', options: { padding: 20 } }],
                      }}
                    >
                      <InfoOutlinedIcon></InfoOutlinedIcon>
                    </MzTooltip>
                  )}
                </>
              )}
            </Box>
          }
        >
          {value === null
            ? null
            : Array.isArray(value)
            ? value.map((item, index) => {
                return (
                  <TreeItem
                    sx={sx.treeItem}
                    itemId={`${parentIndex}${outerIndex}${index}`}
                    label={<Typography sx={sx.label} variant="body2">{`${item['_label']}`}</Typography>}
                  >
                    {renderTree(item, `${parentIndex}${outerIndex}${index}`)}
                  </TreeItem>
                )
              })
            : typeof value === 'object'
            ? renderTree(value, `${parentIndex}${outerIndex}`)
            : null}
        </TreeItem>
      )
    });
  }

  return <SimpleTreeView>{renderTree(data)}</SimpleTreeView>;
}
